import { useState, useMemo, useCallback, type RefObject } from 'react'

import {
  Flex,
  Popover,
  IconButton,
  SavedRepliesIcon,
  Skeleton,
  VisuallyHidden,
} from '@buffer-mono/popcorn'
import type { SavedReply } from '~publish/gql/graphql'
import styles from './SavedRepliesPopover.module.css'
import { SavedReplyForm } from '../SavedReplyForm/SavedReplyForm'
import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import { SavedRepliesPaywall } from '../components/SavedRepliesPaywall/SavedRepliesPaywall'
import { SavedReplyItem } from './SavedReplyItem'
import { SavedReplyHeader } from './SavedReplyHeader'
import { SavedReplyErrorState } from './SavedReplyErrorState'
import { SavedReplyEmptyState } from './SavedReplyEmptyState'
import { useSavedRepliesKeyboardNav } from './hooks/useSavedRepliesKeyboardNav'
import { useSavedRepliesQuery } from '../hooks/savedReplies/useSavedRepliesQuery'

export const SavedRepliesPopover = ({
  savedRepliesPopoverRef,
  handleInsertSavedReply,
}: {
  savedRepliesPopoverRef: RefObject<HTMLDivElement>
  handleInsertSavedReply: (savedReply: SavedReply) => void
}): JSX.Element => {
  const { savedReplies = [], loading, error } = useSavedRepliesQuery()
  const limits = useCurrentOrganization()?.limits
  const [openForm, setOpenForm] = useState(false)

  const [openPaywall, setOpenPaywall] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(0)

  const hasSavedReplies = !!savedReplies?.length
  const hasReachedLimit =
    (savedReplies?.length ?? 0) > (limits?.savedReplies ?? 0)

  const handleOpenForm = useCallback(
    (open: boolean): void => {
      setActiveIndex(null)
      setOpenForm(open)
    },
    [setActiveIndex, setOpenForm],
  )

  const handleMouseEnter = useCallback((index: number): void => {
    setActiveIndex(index)
  }, [])

  const handleMouseLeave = useCallback((): void => {
    setActiveIndex(null)
  }, [])

  const skeletonLoadingView = useMemo(
    () => (
      <Flex
        direction="column"
        gap="xs"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <VisuallyHidden>Loading saved replies...</VisuallyHidden>
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} width="100%" height="72px" />
        ))}
      </Flex>
    ),
    [],
  )

  const memoizedSavedRepliesList = useMemo(() => {
    return savedReplies?.map(
      (savedReply: SavedReply, index: number): JSX.Element => (
        <SavedReplyItem
          key={savedReply.id}
          savedReply={savedReply}
          handleInsertSavedReply={handleInsertSavedReply}
          isSelected={activeIndex === index}
          onMouseEnter={(): void => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
        />
      ),
    )
  }, [
    savedReplies,
    handleInsertSavedReply,
    activeIndex,
    handleMouseLeave,
    handleMouseEnter,
  ])

  useSavedRepliesKeyboardNav({
    savedReplies,
    activeIndex,
    setActiveIndex,
  })

  return (
    <Popover>
      <Popover.Trigger>
        <IconButton
          label="Open saved replies"
          variant="tertiary"
          className={styles.triggerButton}
          tooltip="Saved replies"
        >
          <SavedRepliesIcon />
        </IconButton>
      </Popover.Trigger>
      <Popover.Content
        side="top"
        size="medium"
        className={styles.popoverContent}
        ref={savedRepliesPopoverRef}
      >
        {/* Header */}

        <SavedReplyHeader
          setOpenForm={setOpenForm}
          hasReachedLimit={hasReachedLimit}
          hasSavedReplies={hasSavedReplies}
          setOpenPaywall={setOpenPaywall}
        />

        {/* New Reply Form */}
        <SavedReplyForm edit={false} open={openForm} setOpen={handleOpenForm} />

        {/* Loading State */}
        {loading && skeletonLoadingView}

        {/* Error State */}
        {error && <SavedReplyErrorState />}

        {/* Saved replies List */}
        {!loading && !error && hasSavedReplies && (
          <Flex
            direction="column"
            gap="xs"
            role="listbox"
            aria-label="Saved replies list"
            tabIndex={0}
          >
            {memoizedSavedRepliesList}
          </Flex>
        )}

        {/* Empty state */}
        {!loading && !error && !openForm && !hasSavedReplies && (
          <SavedReplyEmptyState setOpenForm={handleOpenForm} />
        )}

        <SavedRepliesPaywall
          open={openPaywall}
          setOpen={setOpenPaywall}
          upgradePathName="community-saved-replies-paywall"
          ctaProperties={{
            view: 'community',
            location: 'saved-replies-popover',
            button: 'upgrade',
          }}
        />
      </Popover.Content>
    </Popover>
  )
}
